import React, { useState, useEffect, lazy, Suspense, useRef } from "react";
import api from "../../services/Api";
import { Spin, Alert } from "antd";
import { Link, useParams } from "react-router-dom";
import AuctionList from "./AuctionList";
import { Helmet } from "react-helmet-async";
import { useMapsLibrary } from "@vis.gl/react-google-maps";

// Lazy load Map components
const APIProvider = lazy(() => import("@vis.gl/react-google-maps").then((mod) => ({ default: mod.APIProvider })));
const Map = lazy(() => import("@vis.gl/react-google-maps").then((mod) => ({ default: mod.Map })));
const AdvancedMarker = lazy(() => import("@vis.gl/react-google-maps").then((mod) => ({ default: mod.AdvancedMarker })));

const AuctionCategoryPage = () => {
    const { typeslug, areaslug, weekslug } = useParams();

    const auctionTypeTranslations = [
        { type: "sale", translation: "Myydään lomaosake" },
        { type: "rent", translation: "Vuokrataan lomaosake" },
        { type: "auction", translation: "Huutokaupataan lomaosake" },
    ];

    const auctionTypeSlugs = [
        { type: "sale", slug: "myydaan-lomaosake" },
        { type: "rent", slug: "vuokrataan-lomaosake" },
        { type: "auction", slug: "huutokaupataan-lomaosake" },
    ];

    const auctionType = auctionTypeSlugs.find((t) => t.slug === typeslug)?.type;

    const [filters, setFilters] = useState({ types: [] });
    const [areas, setAreas] = useState([]);
    const [loadingAreas, setLoadingAreas] = useState(true);
    const [area, setArea] = useState({});
    const [center, setCenter] = useState({ lat: 37.7749, lng: -122.4194 });
    const [areaError, setAreaError] = useState(null);
    const geocoding = useMapsLibrary("geocoding");
    const [linkPrefix, setLinkPrefix] = useState("");

    const auctionTypeTranslation = auctionTypeTranslations.find((t) => t.type === auctionType)?.translation;
    const areaData = areas.find((a) => a.slug === areaslug);
    const showMap = !!areaslug && !!area?.name;

    // Update filters based on slug params
    useEffect(() => {
        const newFilters = { types: [auctionType] };

        if (areaslug) {
            newFilters.areaslugs = [areaslug];
            setLinkPrefix(`/${auctionTypeSlugs.find((t) => t.type === auctionType).slug}/a/${areaslug}`);
        } else {
            setLinkPrefix(`/${auctionTypeSlugs.find((t) => t.type === auctionType).slug}`);
        }

        if (weekslug) {
            const weekNumber = parseInt(weekslug.replace("viikko-", ""), 10);

            if (!isNaN(weekNumber)) {
                newFilters.propertyWeeks = [weekNumber + "-" + weekNumber];
            }
        }

        setFilters(newFilters);
    }, [auctionType, areaslug, weekslug]);

    useEffect(() => {
        const fetchAreas = async () => {
            setLoadingAreas(true);
            try {
                const response = await api.getAvailableAreasAuctions();
                setAreas(response);
            } catch (error) {
                console.error("Error fetching areas:", error);
            } finally {
                setLoadingAreas(false);
            }
        };

        fetchAreas();
    }, []);

    useEffect(() => {
        const fetchArea = async () => {
            if (!areaslug) return;
            setAreaError(null);
            try {
                const response = await api.getAreaBySlug(areaslug);
                setArea(response);
            } catch (error) {
                console.error("Error fetching area:", error);
                setAreaError("Aluetta ei löytynyt tai sitä ei voitu ladata.");
            }
        };

        fetchArea();
    }, [areaslug]);

    useEffect(() => {
        const geocodeAddress = async (address) => {
            if (!geocoding) return;
            const geocoder = new geocoding.Geocoder();
            return new Promise((resolve, reject) => {
                geocoder.geocode({ address }, (results, status) => {
                    if (status === "OK" && results[0]?.geometry?.location) {
                        resolve({
                            lat: results[0].geometry.location.lat(),
                            lng: results[0].geometry.location.lng(),
                        });
                    } else {
                        reject(status);
                    }
                });
            });
        };

        if (area.name && geocoding) {
            geocodeAddress(area.name)
                .then(setCenter)
                .catch((err) => console.error("Geocode failed:", err));
        }
    }, [geocoding, area]);

    const [faqExpanded, setFaqExpanded] = useState(false);
    const faqRef = useRef(null);
    const [faqTooTall, setFaqTooTall] = useState(false);

    useEffect(() => {
        if (faqRef.current) {
            setFaqTooTall(faqRef.current.scrollHeight > 100);
        }
    }, [area.seoFaq]);

    return (
        <>
            {!loadingAreas && (
                <Helmet>
                    <title>
                        {auctionTypeTranslation}
                        {areaData ? ` - ${areaData.area}` : ""} - Lomahuuto.fi
                    </title>
                    <meta name="title" content={`${auctionTypeTranslation}${areaData ? ` - ${areaData.area}` : ""}`} />
                    <meta
                        property="og:title"
                        content={`${auctionTypeTranslation}${areaData ? ` - ${areaData.area}` : ""}`}
                    />
                </Helmet>
            )}
            <div className="container max-w-screen-2xl m-auto text-lg md:p-5 md:px-0">
                {!loadingAreas && (
                    <nav aria-label="breadcrumb" className="flex items-center text-gray-500 text-sm space-x-2 mb-5">
                        <Link to="/">Etusivu</Link>
                        <span className="mx-2">/</span>
                        <Link to={`/${auctionTypeSlugs.find((t) => t.type === auctionType).slug}`}>
                            {auctionTypeTranslation}
                        </Link>
                        {areaslug && areaData && (
                            <>
                                <span className="mx-2">/</span>
                                <Link
                                    to={`/${auctionTypeSlugs.find((t) => t.type === auctionType).slug}/a/${areaslug}`}
                                >
                                    {areaData.area}
                                </Link>
                            </>
                        )}
                    </nav>
                )}

                <div className="md:flex">
                    <div className="w-full md:w-1/6 mb-5 md:mb-0">
                        <h2>Alueet</h2>
                        {loadingAreas ? (
                            <Spin />
                        ) : (
                            <ul className="list-none p-0 m-0 text-sm">
                                {areas.map((area) => (
                                    <li key={area.slug}>
                                        <Link
                                            className={`${
                                                areaslug === area.slug ? "font-bold text-primary-200" : ""
                                            } hover:text-blue-500`}
                                            to={`/${auctionTypeSlugs.find((t) => t.type === auctionType).slug}/a/${
                                                area.slug
                                            }`}
                                        >
                                            {area.area}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <div className="flex-1">
                        <h1>
                            {auctionTypeTranslation}
                            {areaData ? ` - ${areaData.area}` : ""}
                        </h1>

                        {areaError && <Alert message={areaError} type="error" />}

                        {area.description && <div dangerouslySetInnerHTML={{ __html: area.description }} />}

                        {filters.types.length === 1 && (
                            <AuctionList filters={filters} showWeeks={true} linkPrefix={linkPrefix} />
                        )}

                        {area.seoDescription && (
                            <div>
                                {/* <h2 className="mt-10">Usein kysytyt kysymykset</h2> */}
                                <div className="mt-10" dangerouslySetInnerHTML={{ __html: area.seoDescription }} />
                            </div>
                        )}

                        {showMap && (
                            <div className="mt-10">
                                <Suspense fallback={<Spin />}>
                                    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                                        <Map
                                            defaultCenter={center}
                                            defaultZoom={10}
                                            mapId="DEMO_MAP_ID"
                                            colorScheme="FOLLOW_SYSTEM"
                                            style={{
                                                marginTop: "20px",
                                                width: "100%",
                                                height: "400px",
                                                borderRadius: "10px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <AdvancedMarker position={center} />
                                        </Map>
                                    </APIProvider>
                                </Suspense>
                            </div>
                        )}

                        {area.seoFaq && (
                            <div>
                                <h2 className="mt-10">Usein kysytyt kysymykset</h2>
                                <div className="relative">
                                    <div
                                        ref={faqRef}
                                        className={`transition-all duration-500 ease-in-out overflow-hidden ${
                                            faqExpanded ? "max-h-[1000px]" : "max-h-[100px]"
                                        }`}
                                    >
                                        <div dangerouslySetInnerHTML={{ __html: area.seoFaq }} />
                                    </div>

                                    {!faqExpanded && faqTooTall && (
                                        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                                    )}
                                </div>

                                {faqTooTall && (
                                    <button
                                        onClick={() => setFaqExpanded(!faqExpanded)}
                                        className="text-blue-600 underline"
                                    >
                                        {faqExpanded ? "Näytä vähemmän" : "Näytä lisää"}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default AuctionCategoryPage;
